/**
 * Cookie Cap Utility
 *
 * Prevents cookie bloat from crashing requests against GCP's hard 64KB header limit.
 *
 * GA4 sets a separate `_ga_XXXXXXXXXX` cookie for every property configured via gtag.
 * When organizers each have their own GA4 property, power users accumulate 70+ GA cookies
 * just by browsing events. Combined with Facebook, Hotjar, TikTok, etc., the Cookie header
 * can exceed the load balancer limit, causing 400/431 errors that lock users out.
 *
 * This module caps `_ga_*` cookies to the N most recent and prunes known stale
 * third-party tracking cookies on app boot.
 */

const MAX_GA_COOKIES = 10;

const ESTIMATED_HEADER_BUDGET_BYTES = 50_000;

const GA_PROPERTY_COOKIE_PATTERN = /^_ga_[A-Z0-9]+$/;

/**
 * Third-party cookie prefixes that accumulate per-property/per-session variants.
 * We prune all but the most recent of each prefix group.
 */
const PRUNABLE_THIRD_PARTY_PREFIXES = [
  "_hjSessionUser_",
  "_hjSession_",
  "_tt_",
] as const;

const MAX_THIRD_PARTY_COOKIES_PER_PREFIX = 3;

interface ParsedCookie {
  name: string;
  value: string;
}

function parseCookies(): ParsedCookie[] {
  if (typeof document === "undefined" || !document.cookie) return [];

  return document.cookie
    .split(";")
    .map((pair) => {
      const eqIndex = pair.indexOf("=");
      if (eqIndex === -1) return null;
      return {
        name: pair.substring(0, eqIndex).trim(),
        value: pair.substring(eqIndex + 1).trim(),
      };
    })
    .filter((c): c is ParsedCookie => c !== null);
}

/**
 * GA4 cookie values encode a session timestamp.
 * Format variants: `GS1.1.<timestamp>.…` or `GS2.1.<timestamp>.…`
 * Returns epoch seconds, or 0 if unparseable (sorts to bottom = oldest = first to prune).
 */
function extractGaCookieTimestamp(value: string): number {
  const parts = value.split(".");
  if (parts.length >= 3) {
    const ts = Number(parts[2]);
    if (!Number.isNaN(ts) && ts > 1_000_000_000) return ts;
  }
  return 0;
}

function deleteCookie(name: string): void {
  if (typeof document === "undefined") return;

  const hostname = window.location?.hostname ?? "";
  const domainParts = hostname ? hostname.split(".") : [];

  const expiry = "Thu, 01 Jan 1970 00:00:00 GMT";

  // GA cookies can be set on the root domain, current domain, or without a domain.
  // Try all likely combinations so the delete actually hits.
  const domainVariants = [""];
  if (domainParts.length >= 2) {
    domainVariants.push(
      `.${domainParts.slice(-2).join(".")}` // e.g. .showpass.com
    );
  }
  if (domainParts.length >= 3) {
    domainVariants.push(`.${hostname}`); // e.g. .www.showpass.com
  }

  domainVariants.forEach((domain) => {
    const domainAttr = domain ? `; domain=${domain}` : "";
    document.cookie = `${name}=; expires=${expiry}; max-age=0; path=/${domainAttr}`;
  });
}

/**
 * Prune `_ga_*` cookies down to the `maxGaCookies` most recent.
 * Returns the number of cookies deleted.
 */
export function pruneGaCookies(maxGaCookies = MAX_GA_COOKIES): number {
  const cookies = parseCookies();
  const gaCookies = cookies.filter((c) =>
    GA_PROPERTY_COOKIE_PATTERN.test(c.name)
  );

  if (gaCookies.length <= maxGaCookies) return 0;

  const sorted = [...gaCookies].sort(
    (a, b) =>
      extractGaCookieTimestamp(b.value) - extractGaCookieTimestamp(a.value)
  );

  const toDelete = sorted.slice(maxGaCookies);
  toDelete.forEach((cookie) => deleteCookie(cookie.name));

  return toDelete.length;
}

/**
 * Prune per-instance third-party cookies (Hotjar sessions, TikTok, etc.)
 * that accumulate one cookie per unique ID.
 */
export function pruneThirdPartyCookies(
  maxPerPrefix = MAX_THIRD_PARTY_COOKIES_PER_PREFIX
): number {
  const cookies = parseCookies();

  return PRUNABLE_THIRD_PARTY_PREFIXES.reduce((totalDeleted, prefix) => {
    const matching = cookies.filter((c) => c.name.startsWith(prefix));
    if (matching.length <= maxPerPrefix) return totalDeleted;

    // Keep the ones with the highest numeric suffixes (most recent IDs tend to be larger).
    // Best-effort heuristic since these cookies don't embed timestamps.
    const sorted = [...matching].sort((a, b) =>
      b.name.localeCompare(a.name, undefined, { numeric: true })
    );

    const toDelete = sorted.slice(maxPerPrefix);
    toDelete.forEach((cookie) => deleteCookie(cookie.name));

    return totalDeleted + toDelete.length;
  }, 0);
}

/**
 * Estimate total cookie header size in bytes.
 * Each cookie is sent as `name=value` with `; ` separators.
 */
export function estimateCookieHeaderSize(): number {
  if (typeof document === "undefined" || !document.cookie) return 0;
  // The raw document.cookie string is close to what gets sent in the Cookie header.
  // +12 accounts for "Cookie: " prefix (8 bytes) + "\r\n" (2 bytes) + safety margin.
  // Using string length is sufficient — cookie names/values are ASCII.
  return document.cookie.length + 12;
}

/**
 * Returns true if the estimated cookie header is approaching the danger zone.
 */
export function isCookieHeaderNearLimit(
  budgetBytes = ESTIMATED_HEADER_BUDGET_BYTES
): boolean {
  return estimateCookieHeaderSize() > budgetBytes;
}

/**
 * Run all cookie pruning strategies. Call on app initialization.
 * Returns a summary of what was cleaned up.
 */
export function runCookieCleanup(maxGaCookies = MAX_GA_COOKIES): {
  gaDeleted: number;
  thirdPartyDeleted: number;
  estimatedSizeAfter: number;
} {
  if (typeof document === "undefined") {
    return { gaDeleted: 0, thirdPartyDeleted: 0, estimatedSizeAfter: 0 };
  }

  const gaDeleted = pruneGaCookies(maxGaCookies);
  const thirdPartyDeleted = pruneThirdPartyCookies();
  const estimatedSizeAfter = estimateCookieHeaderSize();

  return { gaDeleted, thirdPartyDeleted, estimatedSizeAfter };
}

/**
 * Enforce the GA cookie cap before initializing a new GA4 property.
 * Call this BEFORE `gtag("config", tagId)` to ensure there's room.
 */
export function enforceGaCookieCap(maxGaCookies = MAX_GA_COOKIES): void {
  pruneGaCookies(maxGaCookies);
}
